import React, { useMemo } from 'react';
import {
  Platform,
  StyleProp,
  StyleSheet,
  Text,
  TextInput,
  TextInputProps,
  TextStyle,
  View,
  ViewStyle,
} from 'react-native';

import { colors, fonts, scale } from '../Styles';

interface MNOHSTextInputProps extends Omit<TextInputProps, 'underlineColorAndroid' | 'style'> {
  style?: StyleProp<TextStyle>;
  /** Style to be applied to the `TextInput` container view. */
  containerStyle?: ViewStyle;
  /** Gives the TextInput a box-shadow. Defaults to false if not specified. */
  floating?: boolean;
  /** If the text is displayed as disabled */
  disabled?: boolean;
  /** Label of the TextInput */
  title?: string;
  /** Style to be applied to the Title */
  titleStyle?: TextStyle;
  /** Whether or not to display the * to mark as required */
  required?: boolean;
}

/**
 * Custom wrapper around `TextInput` that adds style-guide-defined styles & padding and adds an
 * underline below the input.
 *
 * Note: This components accepts the same props as the React Native `TextInput` props, with the
 * addition of `viewStyle`, for styling the view wrapping the input & underline.
 */
export const MNOHSTextInput = (props: MNOHSTextInputProps) => {
  return (
    <View
      style={useMemo(
        () => ({
          // This paddingTop gets it just right on each platform.
          ...(props.multiline ? styles.multilineContainer : {}),
          ...props.containerStyle,
        }),
        [props.containerStyle, props.multiline]
      )}>
      {
        /** NOTE: This Boolean constructor is here because in order to conditionally render
         * a text component, it's required that you compare against a boolean value, rather than
         * falsey/truthy values. This is outlined in detail here:
         * https://koprowski.it/2020/conditional-rendering-react-native-text-crash/
         */
        Boolean(props.title) && (
          <Text style={[styles.title, props.titleStyle]}>
            {props.title} {props.required && <Text style={[styles.title, styles.asterisk]}>*</Text>}
          </Text>
        )
      }
      <View style={styles.defaultRowContainer}>
        <TextInput
          underlineColorAndroid={'transparent'}
          {...props}
          style={[
            {
              // iOS Shadowing
              shadowColor: props.floating ? colors.black : undefined,
              shadowRadius: props.floating ? 2 : undefined,
              shadowOpacity: props.floating ? 0.5 : undefined,
              shadowOffset: props.floating ? { width: 0, height: 2 } : undefined,
              // Android Shadowing
              elevation: props.floating ? 5 : undefined,
            },
            props.disabled && { color: '#626161' },
            _styles.defaultTextInput,
            props.style,
          ]}
        />
      </View>
    </View>
  );
};

// styles for the web
// if you wrap styling for MNOHSTextInput inside StyleSheet.create
// it doesn't work for some reason
const _styles = {
  defaultTextInput: {
    flex: 1,
    paddingTop: 0,
    paddingBottom: scale(2),
    paddingHorizontal: scale(8),
    outline: 'none',
    backgroundColor: colors.grayLight,
    borderRadius: scale(8),
    minHeight: scale(50),
    ...fonts.paragraph,
  },
};

const styles = StyleSheet.create({
  multilineContainer: {
    borderWidth: scale(1),
    borderRadius: scale(5),
    paddingTop: Platform.OS === 'ios' ? scale(3) : 0,
  },
  defaultRowContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  multilineRowContainer: {
    flexDirection: 'row',
    flex: 1,
  },
  title: {
    ...fonts.linkedText,
    marginBottom: scale(8),
  },
  asterisk: {
    color: colors.science,
  },
});
